import React from 'react';

export const DEFAULT_AGE_BUCKETS_TEXT = '0-30,31-60,61-120,121+';

export function parseAgeBucketsText(text) {
    const raw = (text || DEFAULT_AGE_BUCKETS_TEXT).trim();
    const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
    if (!parts.length) {
        return parseAgeBucketsText(DEFAULT_AGE_BUCKETS_TEXT);
    }
    return parts.map((spec, index) => {
        if (spec.endsWith('+')) {
            const fromDays = parseInt(spec.slice(0, -1), 10);
            if (Number.isNaN(fromDays) || fromDays < 0) {
                throw new Error(`Invalid age bucket "${spec}". Use format like 121+.`);
            }
            return {
                key: `bucket_${index}`,
                from_days: fromDays,
                to_days: null,
                label: `${fromDays}+ Days`,
                spec,
            };
        }
        const [fromText, toText] = spec.split('-');
        const fromDays = parseInt(fromText, 10);
        const toDays = parseInt(toText, 10);
        if (
            Number.isNaN(fromDays)
            || Number.isNaN(toDays)
            || fromDays < 0
            || toDays < fromDays
        ) {
            throw new Error(`Invalid age bucket "${spec}". Use format like 31-60.`);
        }
        return {
            key: `bucket_${index}`,
            from_days: fromDays,
            to_days: toDays,
            label: `${fromDays}-${toDays} Days`,
            spec,
        };
    });
}

export function serializeAgeBuckets(buckets) {
    return buckets.map((bucket) => bucket.spec
        || (bucket.to_days == null ? `${bucket.from_days}+` : `${bucket.from_days}-${bucket.to_days}`)).join(',');
}

export function inferReportDataType(rows) {
    if (!rows?.length) {
        return null;
    }
    const sample = rows.find((row) => row.invoice_no) || rows[0];
    if (sample.invoice_no != null && String(sample.invoice_no).trim() !== '') {
        return 'detail';
    }
    if (sample.total_outstanding != null || sample.bucket_0 != null) {
        return 'summary';
    }
    if (sample.outstanding_amount != null) {
        return 'detail';
    }
    return null;
}

export function buildColumnsForReportType(
    reportType,
    ageBuckets,
    bucketTotals,
    formatAmount = (value) => value,
) {
    if (reportType === 'detail') {
        return buildDetailColumns(bucketTotals, formatAmount);
    }
    return buildSummaryColumns(ageBuckets, bucketTotals, formatAmount);
}

function amountColumnHeader(label, subtotal) {
    return (
        <div style={{ textAlign: 'right', lineHeight: 1.25, width: '100%' }}>
            {subtotal != null && subtotal !== '' ? (
                <div style={{ fontWeight: 700, marginBottom: 2 }}>{subtotal}</div>
            ) : null}
            <div>{label}</div>
        </div>
    );
}

export function buildPdfExportColumns(tableColumns, bucketTotals = {}, formatAmount = (value) => value) {
    return tableColumns.map((col) => {
        const dataKey = col.fieldName;
        let headerSubtotal = '';
        if (dataKey === 'total_outstanding' || dataKey === 'outstanding_amount') {
            headerSubtotal = formatAmount(bucketTotals.grand_total || 0);
        } else if (dataKey?.startsWith('bucket_')) {
            headerSubtotal = formatAmount(bucketTotals[dataKey] || 0);
        }
        return {
            header: col.headerName,
            headerSubtotal,
            dataKey,
            halign: col.align === 'right' ? 'right' : 'left',
            dataBold: dataKey === 'total_outstanding',
            cellWidth: col.pdfWidth,
        };
    });
}

export function buildSummaryColumns(ageBuckets, bucketTotals = {}, formatAmount = (value) => value) {
    const bucketColumns = ageBuckets.map((bucket) => ({
        fieldName: bucket.key,
        headerName: bucket.label,
        align: 'right',
        headerContent: amountColumnHeader(bucket.label, formatAmount(bucketTotals[bucket.key] || 0)),
    }));
    return [
        { fieldName: 'customer_name', headerName: 'Customer', align: 'left', size: 180, minSize: 140, pdfWidth: 38 },
        { fieldName: 'site_name', headerName: 'Site', align: 'left', size: 160, minSize: 120, pdfWidth: 32 },
        { fieldName: 'salesrep_name', headerName: 'Sales Rep', align: 'left', size: 120, minSize: 90, pdfWidth: 22 },
        ...bucketColumns,
        {
            fieldName: 'total_outstanding',
            headerName: 'Total Outstanding',
            align: 'right',
            headerContent: amountColumnHeader(
                'Total Outstanding',
                formatAmount(bucketTotals.grand_total || 0),
            ),
            dataStyle: { fontWeight: 700 },
        },
    ];
}

export function buildDetailColumns(bucketTotals = {}, formatAmount = (value) => value) {
    return [
        { fieldName: 'customer_name', headerName: 'Customer', align: 'left', size: 200, minSize: 140 },
        { fieldName: 'site_name', headerName: 'Site', align: 'left', size: 160, minSize: 120 },
        { fieldName: 'salesrep_name', headerName: 'Sales Rep', align: 'left', size: 120, minSize: 90 },
        { fieldName: 'invoice_no', headerName: 'Invoice No', align: 'left', size: 130, minSize: 100 },
        { fieldName: 'invoice_date', headerName: 'Invoice Date', align: 'left', size: 110, minSize: 95 },
        { fieldName: 'grade', headerName: 'Grade', align: 'left', size: 100, minSize: 80 },
        { fieldName: 'age_days', headerName: 'Age (Days)', align: 'right', size: 90, minSize: 80 },
        { fieldName: 'age_bucket', headerName: 'Age Bucket', align: 'left', size: 110, minSize: 90 },
        {
            fieldName: 'outstanding_amount',
            headerName: 'Outstanding',
            align: 'right',
            size: 130,
            minSize: 110,
            headerContent: amountColumnHeader(
                'Outstanding',
                formatAmount(bucketTotals.grand_total || 0),
            ),
        },
    ];
}
